import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../contexts/PageTitleContext';
import type { TaskTag } from '@/shared/types/taskQueue.types';
import {
  createTask,
  getApiErrorMessage,
  listTaskTags,
} from '../../services/tasksAPI';
import TaskDetailNavbar from './TaskDetailNavbar';
import { buildTaskDetailNavState, parseTaskDetailNavState } from './taskDetailNavState';
import './TaskQueue.css';

const TaskCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = parseTaskDetailNavState(location.state);

  usePageTitle('New Task');

  const [tags, setTags] = useState<TaskTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newSpec, setNewSpec] = useState('');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    void listTaskTags()
      .then(setTags)
      .catch(() => undefined);
  }, []);

  const toggleTag = (tagId: string): void => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  };

  const submitCreate = async (): Promise<void> => {
    if (creating || !newTitle.trim()) return;
    setCreating(true);
    setMessage('');
    try {
      const task = await createTask({
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        spec: newSpec.trim() || null,
        tagIds: selectedTagIds,
      });
      navigate(`/tasks/${task.id}`, {
        replace: true,
        state: buildTaskDetailNavState(navState.fromPath, [task]),
      });
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const activeTags = tags.filter((t) => !t.retired);

  return (
    <div className="task-detail-page">
      <TaskDetailNavbar title="New task" showSiblingNav={false} />
      {message ? <p className="task-message">{message}</p> : null}

      <div className="task-form">
        <label>
          Title
          <input
            value={newTitle}
            disabled={creating}
            onChange={(e) => setNewTitle(e.target.value)}
          />
        </label>
        <label>
          Description
          <textarea
            value={newDescription}
            disabled={creating}
            onChange={(e) => setNewDescription(e.target.value)}
          />
        </label>
        <label>
          Spec (Markdown)
          <textarea
            value={newSpec}
            disabled={creating}
            onChange={(e) => setNewSpec(e.target.value)}
          />
        </label>

        <div className="task-tag-filter" role="group" aria-label="Task tags">
          {activeTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className={`task-tag-chip ${selectedTagIds.includes(tag.id) ? 'selected' : ''}`}
              disabled={creating}
              onClick={() => toggleTag(tag.id)}
            >
              {tag.name}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="primary"
          disabled={creating || !newTitle.trim()}
          aria-busy={creating}
          onClick={() => void submitCreate()}
        >
          {creating ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
};

export default TaskCreatePage;
